import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from "react-redux";
import DataTable from 'react-data-table-component';
import { UserRolesService } from "../../../services/user/userroles.services";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from '../../common/notification/toaster_notify.component';

const validationSchema = Yup.object({
    roleName: Yup.string()
        .required('Role Name is required')
        .min(3, 'Role Name must be at least 3 characters long')
});

const AddUserRoleComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const selectedStore = useSelector((state) => state?.user?.selectedStoreId);
    const roleId = location.state?.roleId || 0;
    const isEditMode = !!roleId;

    const [loading, setLoading] = useState(false);
    const [userPermissions, setUserPermissions] = useState([
        { module: 'Modules', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Company', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'User Role', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Users', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'User Profile', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Categories', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Items', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Countries', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'States', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Statuses', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Tags', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Tax Group', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Lead Sources', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Priorities', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Probability', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Leads', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Activities', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Customers', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Quotes', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Invoices', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Tender', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Services', canView: false, canCreate: false, canUpdate: false, canDelete: false },
        { module: 'Search', canView: false, canCreate: false, canUpdate: false, canDelete: false },
    ]);

    const formik = useFormik({
        initialValues: {
            roleId: 0,
            roleName: '',
            isActive: true,
            storeId: selectedStore,
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                if (isEditMode) {
                    await UserRolesService.updateUserRoleById(roleId, values)
                        .then((response) => {
                            setLoading(false);
                            if (response) {
                                navigate('/userrole');
                            }
                        })
                } else {
                    await UserRolesService.addNewUserRole(values)
                        .then((response) => {
                            setLoading(false);
                            if (response) {
                                navigate('/userrole');
                            }
                        })
                }
            }
            catch (error) {
                handleNotify('Error while add userrole', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT)
            }
        },
    });

    useEffect(() => {
        if (isEditMode) {
            const fetchUserRoleDetail = async () => {
                try {
                    const data = await UserRolesService.getUserRoleById(roleId)
                    formik.setValues({
                        roleId: data?.roleId,
                        roleName: data?.roleName,
                        isActive: data?.isActive,
                    });
                } catch (error) {
                    console.log("Error fetching userrole details", error);
                    handleNotify(
                        "Error fetching userrole details.",
                        TOASTER_TYPE.ERROR,
                        TOASTER_POSITION.TOP_RIGHT
                    );
                }
            };
            fetchUserRoleDetail();
        }
    }, []);


    const handlePermissionChange = (index, permissionType) => {
        const updatedPermissions = [...userPermissions];
        updatedPermissions[index][permissionType] = !updatedPermissions[index][permissionType];
        setUserPermissions(updatedPermissions);
    };

    const columns = [
        {
            name: 'Module',
            selector: row => row.module,
            sortable: true,
        },
        {
            name: 'View',
            cell: (row, index) => (
                <input
                    type="checkbox"
                    checked={row.canView}
                    onChange={() => handlePermissionChange(index, 'canView')}
                />
            ),
            center: true,
        },
        {
            name: 'Create',
            cell: (row, index) => (
                <input
                    type="checkbox"
                    checked={row.canCreate}
                    onChange={() => handlePermissionChange(index, 'canCreate')}
                />
            ),
            center: true,
        },
        {
            name: 'Update',
            cell: (row, index) => (
                <input
                    type="checkbox"
                    checked={row.canUpdate}
                    onChange={() => handlePermissionChange(index, 'canUpdate')}
                />
            ),
            center: true,
        },
        {
            name: 'Delete',
            cell: (row, index) => (
                <input
                    type="checkbox"
                    checked={row.canDelete}
                    onChange={() => handlePermissionChange(index, 'canDelete')}
                />
            ),
            center: true,
        }
    ];

    const handleBackClick = () => {
        navigate("/userrole");
    };

    return (
        <>
            <div className="page-wrapper m-0">
                <div className="page-content">
                    <div className="page-breadcrumb d-flex align-items-center mb-3">
                        <div
                            className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
                            onClick={handleBackClick}
                        >
                            User Role</div>
                        <div className="ps-3">
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb mb-0 p-0">
                                    <li className="breadcrumb-item active" aria-current="page">
                                        {isEditMode ? "Edit UserRole" : "New UserRole"}
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="ms-auto">
                            <button
                                type="button"
                                className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                                onClick={handleBackClick}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                    <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                        <div className="card-body form-container">
                            <form onSubmit={formik.handleSubmit}>
                                <div className="col-md-6 mb-5">
                                    <label htmlFor="roleName" className="form-label d-flex">
                                        Role Name<span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="text"
                                        id="roleName"
                                        name="roleName"
                                        className={`form-control ${formik.touched.roleName && formik.errors.roleName ? "is-invalid" : ""}`}
                                        placeholder="Enter Role Name"
                                        value={formik.values.roleName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.roleName && formik.errors.roleName && (
                                        <div className="invalid-feedback d-flex">{formik.errors.roleName}</div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <div className="table-responsive">
                                        <DataTable
                                            columns={columns}
                                            data={userPermissions}
                                            noHeader
                                            responsive
                                            highlightOnHover
                                        />
                                    </div>
                                </div>
                                {/* Status Radio Buttons */}
                                <div className="d-flex align-items-center mb-4">
                                    <label className="form-label me-3">Status:</label>
                                    <div className="form-check form-check-inline me-3">
                                        <input
                                            type="radio"
                                            id="isActive"
                                            name="isActive"
                                            className="form-check-input"
                                            value={true}
                                            checked={formik.values.isActive === true}
                                            onChange={() => formik.setFieldValue("isActive", true)}
                                        />
                                        <label className="form-check-label" htmlFor="active">
                                            Active
                                        </label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input
                                            type="radio"
                                            id="inactive"
                                            name="isActive"
                                            className="form-check-input"
                                            value={false}
                                            checked={formik.values.isActive === false}
                                            onChange={() => formik.setFieldValue("isActive", false)}
                                        />
                                        <label className="form-check-label" htmlFor="inactive">
                                            Inactive
                                        </label>
                                    </div>
                                </div>

                                <hr />
                                <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                                        onClick={handleBackClick}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                                        disabled={loading}
                                    >
                                        {loading ? "Saving..." : isEditMode ? "Update" : "Save"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddUserRoleComponent;
